import React, { useMemo, useState } from 'react';
import { CheckIcon, GlobeAltIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useLanguage } from './LanguageContext';

const LanguageSetupModal = ({ forceOpen = false }) => {
  const {
    language,
    languages,
    setupRequired,
    preferencesReady,
    savingLanguage,
    previewLanguage,
    completeLanguageSetup,
    t,
  } = useLanguage();
  const [search, setSearch] = useState('');
  const [saveWarning, setSaveWarning] = useState('');

  const visibleLanguages = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return languages;
    return languages.filter((item) => (
      item.name.toLocaleLowerCase().includes(query)
      || item.nativeName.toLocaleLowerCase().includes(query)
    ));
  }, [languages, search]);

  if (!preferencesReady || (!setupRequired && !forceOpen)) return null;

  const handleContinue = async () => {
    setSaveWarning('');
    const result = await completeLanguageSetup(language);
    if (!result.persisted) {
      setSaveWarning(t('Your choice is saved on this device and will sync to your account when the connection returns.'));
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/75 p-3 backdrop-blur-sm sm:p-6" role="presentation">
      <section
        className="theme-panel theme-border flex max-h-[min(760px,94vh)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="language-setup-title"
        data-testid="language-setup-modal"
      >
        <header className="theme-border border-b px-5 py-5 sm:px-7">
          <div className="flex items-start gap-3">
            <div className="theme-accent-soft theme-accent grid h-11 w-11 shrink-0 place-items-center rounded-xl">
              <GlobeAltIcon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="theme-accent text-xs font-bold uppercase tracking-[0.16em]">{t('Welcome to Bandz Journal')}</p>
              <h1 id="language-setup-title" className="theme-text mt-1 text-2xl font-bold tracking-tight">
                {t('Choose your language')}
              </h1>
              <p className="theme-text-muted mt-1 text-sm leading-relaxed">
                {t('This changes the language used for navigation, settings, and guidance throughout Bandz Journal. You can change it anytime in Settings.')}
              </p>
            </div>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-7">
          <label className="relative mb-4 block">
            <span className="sr-only">{t('Search languages')}</span>
            <MagnifyingGlassIcon className="theme-text-muted pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('Search languages')}
              className="theme-input theme-border w-full rounded-xl border py-3 pl-10 pr-4 text-sm outline-none"
            />
          </label>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3" role="radiogroup" aria-label={t('Available languages')}>
            {visibleLanguages.map((item) => {
              const selected = item.code === language;
              return (
                <button
                  key={item.code}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => previewLanguage(item.code)}
                  className={`theme-border flex min-h-[64px] items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition ${
                    selected ? 'theme-accent-soft ring-2 ring-current' : 'theme-hover-surface'
                  }`}
                  data-language-code={item.code}
                  data-i18n-ignore
                >
                  <span className="theme-panel-alt theme-text-muted grid h-9 w-9 shrink-0 place-items-center rounded-lg text-xs font-bold uppercase">
                    {item.code.split('-')[0]}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="theme-text block truncate text-sm font-semibold">{item.nativeName}</span>
                    <span className="theme-text-muted block truncate text-xs">{item.name}</span>
                  </span>
                  {selected ? <CheckIcon className="theme-accent h-5 w-5 shrink-0" /> : null}
                </button>
              );
            })}
          </div>

          <p className="theme-text-muted mt-4 text-xs leading-relaxed">
            {t('Your trades, notes, account names, and ticker symbols stay exactly as you entered them.')}
          </p>
          {saveWarning ? <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-300">{saveWarning}</p> : null}
        </div>

        <footer className="theme-border flex items-center justify-between gap-3 border-t px-5 py-4 sm:px-7">
          <span className="theme-text-muted hidden text-xs sm:block">{languages.length} {t('languages available')}</span>
          <button
            type="button"
            onClick={handleContinue}
            disabled={savingLanguage}
            className="theme-btn-primary ml-auto inline-flex min-h-11 min-w-36 items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold disabled:cursor-wait disabled:opacity-60"
          >
            {savingLanguage ? t('Saving...') : t('Continue')}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default LanguageSetupModal;
